'use client'

import { DUR, EASE } from '@/animations/settings/vars'
import { useGSAPContext, useSanityFilterSync } from '@/hooks'
import { useListScrollRestore } from '@/hooks/use-list-scroll-restore'
import { cn } from '@/lib/utils'
import {
  Button,
  CardMain,
  Container,
  Section,
  SectionHeaderContent,
  SpotSanityError,
  SpotSanityFilters
} from '@/ui/components'
import {
  getGroupedFacets,
  getMatchedFacets,
  getTitleTag,
  sorting
} from '@/utils'
import { ScrollTrigger, gsap } from '@gsap'
import { memo, useEffect, useRef, useState } from 'react'

import {
  useStoreAccomodation,
  useStoreCulture,
  useStoreEatDrink,
  useStoreFreetime,
  useStoreI18n,
  useStoreNature,
  useStoreService,
  useStoreShopping
} from '@/store'
import {
  Typo3CultureItem,
  Typo3SectionSpotSanityAccomodation,
  Typo3SectionSpotSanityCulture,
  Typo3SectionSpotSanityEatDrink,
  Typo3SectionSpotSanityFreetime,
  Typo3SectionSpotSanityNature,
  Typo3SectionSpotSanityService,
  Typo3SectionSpotSanityShopping,
  Typo3SectionSpotSanitySport,
  Typo3SectionSpotSanitySportServices
} from '@/types/typo3'

// TODO: check culture and sportservice stores, originally it was the same store for culture and sportservice
interface Props {
  data:
    | Typo3SectionSpotSanityShopping
    | Typo3SectionSpotSanityFreetime
    | Typo3SectionSpotSanityEatDrink
    | Typo3SectionSpotSanityNature
    | Typo3SectionSpotSanityService
    | Typo3SectionSpotSanitySportServices
    | Typo3SectionSpotSanityAccomodation
    | Typo3SectionSpotSanityCulture
    | Typo3SectionSpotSanitySport
  storeType:
    | 'culture'
    | 'eatdrink'
    | 'freetime'
    | 'nature'
    | 'service'
    | 'sportservice'
    | 'accommodation'
}

const List = ({ data, storeType }: Props) => {
  const D = useStoreI18n.use.dictionary()

  const c = data?.content
  const limit = data?.plugin?.limitItems || 9
  const didMountRef = useRef(false)
  const firstAnimSkipRef = useRef(true)
  const ref = useRef<HTMLElement>(null)
  const eventsRef = useRef<HTMLDivElement>(null)

  const tl = useRef<GSAPTimeline | null>(null)
  const [animate, setAnimate] = useState(false)

  const [filteredItems, setFilteredItems] = useState<Typo3CultureItem[]>([])
  const [delayedItems, setDelayedItems] = useState<Typo3CultureItem[]>([])

  const [filterUrlForBackend, setFilterUrlForBackend] = useState('')
  const linkToPdfBackend = data?.plugin?.linkToPdf

  const stores = {
    culture: useStoreCulture,
    shopping: useStoreShopping,
    eatdrink: useStoreEatDrink,
    freetime: useStoreFreetime,
    nature: useStoreNature,
    service: useStoreService,
    sportservice: useStoreCulture,
    accommodation: useStoreAccomodation
  }

  const store = stores[storeType]

  const {
    facets,
    setFacets,
    setStoreItemsToShow,
    setStoreFilteredItems,
    startDate,
    endDate,
    isOpenToday,
    setIsOpenToday,
    setIsReady,
    search,
    sortBy,
    reset,
    setSearch
  } = store()

  // Reset filters when page is refreshed
  // useEffect(() => {
  //   window.addEventListener('beforeunload', handleRefresh)
  //   return () => {
  //     window.removeEventListener('beforeunload', handleRefresh)
  //   }
  // }, [])

  // const handleRefresh = (e: BeforeUnloadEvent) => {
  //   e.preventDefault()
  //   setFilteredItems([])
  //   setDelayedItems([])
  //   reset()
  //   setItemsToShow(limit)
  //   console.log('handleRefresh')
  //   e.returnValue = ''
  // }

  // Animations
  useGSAPContext({
    scope: eventsRef,
    callback() {
      tl.current = gsap.timeline({
        paused: true,
        defaults: { ease: EASE.out, duration: DUR.default / 2 }
      })
      tl.current.to(eventsRef.current, { opacity: 0 })
    }
  })

  // Filter items based on facets
  useEffect(() => {
    // TODO: Check this
    // @ts-expect-error expected error
    const groupedFacets = getGroupedFacets(facets, c?.facets)

    // Adjust itemsToShow based on filteredItems length but not more than 10
    // Combine facet and date range filtering
    const filtered = c?.items?.filter((item) => {
      // Facet filtering
      const matchesFacets = getMatchedFacets(facets, groupedFacets, item)

      const openToday = isOpenToday
        ? item?.openings?.isOpenToday === true
        : true

      const searchMatch =
        search && search.trim() !== ''
          ? item?.title?.toLowerCase().includes(search.toLowerCase()) ||
            item?.summary?.toLowerCase().includes(search.toLowerCase())
          : true

      // Item must match both facet and date range criteria
      return matchesFacets && openToday && searchMatch
    })

    //Sorting
    const sorted = sorting(filtered, sortBy)

    setFilteredItems(sorted as Typo3CultureItem[])
  }, [
    c,
    facets,
    startDate,
    endDate,
    isOpenToday,
    search,
    sortBy,
    storeType,
    store
  ])

  useSanityFilterSync({
    data,
    setFacets,
    setIsOpenToday,
    setSearch,
    facets,
    search,
    isOpenToday,
    linkToPdfBackend,
    filterUrlForBackend,
    setFilterUrlForBackend
  })

  // common for all lists
  useEffect(() => {
    if (!didMountRef.current && filteredItems?.length > 0) {
      didMountRef.current = true
      setDelayedItems(filteredItems)
      return
    }

    setStoreFilteredItems(filteredItems?.length)

    const timer = setTimeout(() => setDelayedItems(filteredItems), 500)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems])

  const handleLoadMore = () => {
    gsap.to(eventsRef.current, {
      duration: DUR.default / 2,
      opacity: 0,
      onComplete: () => {
        setItemsToShow((prev) => Math.min(prev + limit, filteredItems?.length))
        gsap.to(eventsRef.current, { duration: DUR.default / 2, opacity: 1 })
        gsap.delayedCall(0.25, () => ScrollTrigger.refresh())
      }
    })
  }
  const { saveItemsCount, getSavedItemsCount } = useListScrollRestore({
    onLoadMore: handleLoadMore
  })

  const [itemsToShow, setItemsToShow] = useState(() => {
    try {
      const saved = getSavedItemsCount()
      return saved || limit
    } catch {
      return limit
    }
  })

  useEffect(() => {
    // Adjust itemsToShow based on filteredItems length but not more than 10
    setStoreFilteredItems(filteredItems.length)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredItems.length])

  useEffect(() => {
    // Adjust itemsToShow based on filteredItems length but not more than 10
    setStoreItemsToShow(itemsToShow)
    saveItemsCount(itemsToShow)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsToShow])

  //handlers
  useEffect(() => {
    if (firstAnimSkipRef.current) {
      firstAnimSkipRef.current = false
      return
    }
    setAnimate(true)
    const timer = setTimeout(() => setAnimate(false), 500)
    return () => clearTimeout(timer)
  }, [filteredItems])

  useEffect(() => {
    if (animate) {
      tl.current?.play()
    } else {
      tl.current?.reverse()
    }
  }, [animate])

  //reset all filters if noFilter component is present
  useEffect(() => {
    if (data?.plugin?.hasShowFilter === false) {
      reset()
      setIsReady(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.plugin?.hasShowFilter])

  return (
    <Section data={data} className="s-spotsanity-list" anim="fade-in" ref={ref}>
      <SectionHeaderContent
        titleLayout={c?.titleLayout}
        as={getTitleTag(c?.titleLayout)}
      >
        {c?.title}
      </SectionHeaderContent>
      <Container>
        {c?.buttons && c?.buttons?.length > 0 && (
          <div className="gap-gutter mt-xs mb-lg flex">
            {c?.buttons.map((button, index) => (
              <Button {...button} key={index?.toString()}>
                {button?.label}
              </Button>
            ))}
          </div>
        )}
        {data.plugin?.hasShowFilter && (
          <SpotSanityFilters
            data={data}
            store={store}
            filterUrlForBackend={filterUrlForBackend}
          />
        )}

        <div
          className={cn(
            'gap-x-xs gap-y-lg grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3'
          )}
          ref={eventsRef}
        >
          {delayedItems &&
            delayedItems?.length > 0 &&
            delayedItems
              .slice(0, itemsToShow)
              .map((item) => (
                <CardMain
                  title={item.title}
                  link={{ href: item.slug }}
                  image={item.images?.[0]}
                  subTitle={item?.summary}
                  location={item?.contact?.city}
                  openings={item.openings}
                  todayOpen={item?.openings?.isOpenToday ? 'open' : 'closed'}
                  key={item.uid}
                />
              ))}
        </div>
        <div>
          {delayedItems && itemsToShow < filteredItems?.length && (
            <div className="mt-lg flex justify-center">
              <Button
                variant="secondary"
                onClick={handleLoadMore}
                nextLink={false}
                as="button"
                aria-label={`Load more items. Currently showing ${itemsToShow} of ${filteredItems?.length} items`}
              >
                {D['fe.plugin.filters.loadMore']}
              </Button>
            </div>
          )}
          {delayedItems && delayedItems?.length === 0 && (
            <SpotSanityError store={store} />
          )}
        </div>
      </Container>
    </Section>
  )
}

export default memo(List)
